import { gql } from '@apollo/client';

const DOCUMENT_RESPONSE_SET_FIELDS = gql`
  fragment DocumentResponseSetFields on DocumentResponseSet {
    id
    wmClientId
    name
    type
    allowOther
    allowNone
    isScoring
    responses {
      name
      colour
      score
      flagged
    }
    columns {
      key
      name
      showInDropdown
      showColumnNameInDropdown
    }
    tableRows {
      id
      values
    }
    scoreColumnKey
    flaggedColumnKey
    maxScoreOverride
    labelColumnKey
    valueColumnKey
    allowUserEntry
    userEntryFlagged
    userEntryScore
    userEntryValue
  }
`;

export const DOCUMENT_RESPONSE_SETS_QUERY = gql`
  ${DOCUMENT_RESPONSE_SET_FIELDS}
  query documentResponseSets(
    $filter: DocumentResponseSetFilter
    $pagination: Pagination
  ) {
    documentResponseSets(filter: $filter, pagination: $pagination) {
      edges {
        ...DocumentResponseSetFields
      }
      pageInfo {
        edges
        pages
      }
    }
  }
`;

export const DOCUMENT_RESPONSE_SET_QUERY = gql`
  ${DOCUMENT_RESPONSE_SET_FIELDS}
  query documentResponseSet($id: ID!) {
    documentResponseSet(id: $id) {
      ...DocumentResponseSetFields
    }
  }
`;

export const CREATE_DOCUMENT_RESPONSE_SET = gql`
  ${DOCUMENT_RESPONSE_SET_FIELDS}
  mutation createDocumentResponseSet($input: CreateDocumentResponseSetInput!) {
    createDocumentResponseSet(input: $input) {
      success
      message
      documentResponseSet {
        ...DocumentResponseSetFields
      }
    }
  }
`;

export const UPDATE_DOCUMENT_RESPONSE_SET = gql`
  ${DOCUMENT_RESPONSE_SET_FIELDS}
  mutation updateDocumentResponseSet($input: UpdateDocumentResponseSetInput!) {
    updateDocumentResponseSet(input: $input) {
      success
      message
      documentResponseSet {
        ...DocumentResponseSetFields
      }
    }
  }
`;

export const DELETE_DOCUMENT_RESPONSE_SET = gql`
  mutation deleteDocumentResponseSet($input: DeleteDocumentResponseSetInput!) {
    deleteDocumentResponseSet(input: $input) {
      success
      message
    }
  }
`;

// Entities-group element data API — picker rows scoped to the
// form-owning client. The `context` arg carries either a formOid
// (authenticated) or a publicSlug (anonymous); see
// `FormFillContext` for how the runtime threads those down.
export const DOCUMENT_ELEMENT_MANAGED_PROPERTIES_QUERY = gql`
  query documentElementManagedProperties(
    $context: DocumentElementFormContextInput!
    $search: String
    $pagination: Pagination
  ) {
    documentElementManagedProperties(
      context: $context
      search: $search
      pagination: $pagination
    ) {
      edges {
        id
        name
        addressFormatted
        refId
        customerName
      }
      pageInfo {
        page
        limit
        hasNextPage
      }
    }
  }
`;

export const DOCUMENT_ELEMENT_CONTACTS_QUERY = gql`
  query documentElementContacts(
    $context: DocumentElementFormContextInput!
    $search: String
    $pagination: Pagination
  ) {
    documentElementContacts(
      context: $context
      search: $search
      pagination: $pagination
    ) {
      edges {
        id
        name
        email
        mobile
        businessName
      }
      pageInfo {
        page
        limit
        hasNextPage
      }
    }
  }
`;

export const DOCUMENT_ELEMENT_JOBS_QUERY = gql`
  query documentElementJobs(
    $context: DocumentElementFormContextInput!
    $search: String
    $pagination: Pagination
  ) {
    documentElementJobs(
      context: $context
      search: $search
      pagination: $pagination
    ) {
      edges {
        id
        name
        refId
        status
        propertyName
        customerName
      }
      pageInfo {
        page
        limit
        hasNextPage
      }
    }
  }
`;

export const DOCUMENT_ELEMENT_ASSETS_QUERY = gql`
  query documentElementAssets(
    $context: DocumentElementFormContextInput!
    $search: String
    $pagination: Pagination
  ) {
    documentElementAssets(
      context: $context
      search: $search
      pagination: $pagination
    ) {
      edges {
        id
        name
        refId
        propertyName
        customerName
      }
      pageInfo {
        page
        limit
        hasNextPage
      }
    }
  }
`;

export const DOCUMENT_ELEMENT_USERS_QUERY = gql`
  query documentElementUsers(
    $context: DocumentElementFormContextInput!
    $search: String
    $pagination: Pagination
  ) {
    documentElementUsers(
      context: $context
      search: $search
      pagination: $pagination
    ) {
      edges {
        id
        name
        email
        jobTitle
        preferredName
      }
      pageInfo {
        page
        limit
        hasNextPage
      }
    }
  }
`;

export const DOCUMENT_ELEMENT_PROJECTS_QUERY = gql`
  query documentElementProjects(
    $context: DocumentElementFormContextInput!
    $search: String
    $pagination: Pagination
  ) {
    documentElementProjects(
      context: $context
      search: $search
      pagination: $pagination
    ) {
      edges {
        id
        name
        nameAbbr
        hexColor
        customerName
      }
      pageInfo {
        page
        limit
        hasNextPage
      }
    }
  }
`;

export const DOCUMENT_ELEMENT_CUSTOMERS_QUERY = gql`
  query documentElementCustomers(
    $context: DocumentElementFormContextInput!
    $search: String
    $pagination: Pagination
  ) {
    documentElementCustomers(
      context: $context
      search: $search
      pagination: $pagination
    ) {
      edges {
        id
        name
      }
      pageInfo {
        page
        limit
        hasNextPage
      }
    }
  }
`;

/** The Asset element's class filter setting: the workspace's asset classes. */
export const DOCUMENT_ELEMENT_ASSET_CLASSES_QUERY = gql`
  query documentElementAssetClasses(
    $context: DocumentElementFormContextInput
    $search: String
    $pagination: Pagination
  ) {
    documentElementAssetClasses(
      context: $context
      search: $search
      pagination: $pagination
    ) {
      edges {
        id
        name
        path
      }
      pageInfo {
        page
        limit
        hasNextPage
      }
    }
  }
`;
